import { format, parseISO } from 'date-fns';

export const BUSINESS_TZ = 'America/Vancouver';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const NAIVE_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

const formatterFor = (timeZone) => new Intl.DateTimeFormat('en-US', {
  timeZone, hourCycle: 'h23',
  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
});
const pacificFormatter = formatterFor(BUSINESS_TZ);
// BC moved to permanent UTC-7 on 2026-11-01 (tzdata 2026c); older browser time data still switches to UTC-8
const permanentFormatter = formatterFor('Etc/GMT+7');
const PERMANENT_FROM = Date.UTC(2026, 10, 1, 9, 0);

const zonedParts = (date) => {
  const fmt = date.getTime() >= PERMANENT_FROM ? permanentFormatter : pacificFormatter;
  const p = Object.fromEntries(fmt.formatToParts(date).map((x) => [x.type, x.value]));
  return [+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second];
};

// A local Date whose wall-clock fields equal the Pacific wall-clock time of `date`
export const toBusinessWallClock = (date) => new Date(...zonedParts(date));

export const toDate = (value) => {
  if (value instanceof Date) return value;
  if (typeof value === 'string' && (DATE_ONLY.test(value) || NAIVE_DATETIME.test(value))) return parseISO(value);
  return new Date(value);
};

// Format any timestamp in Pacific time. Date-only and naive strings are shown as entered.
export const fmtTz = (value, pattern) => {
  if (value === null || value === undefined || value === '') return '';
  const isNaive = typeof value === 'string' && (DATE_ONLY.test(value) || NAIVE_DATETIME.test(value));
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return '';
  return format(isNaive ? d : toBusinessWallClock(d), pattern);
};

// UTC ISO -> 'yyyy-MM-ddTHH:mm' in Pacific time (for datetime-local inputs)
export const isoToBusinessInput = (iso) => (iso ? fmtTz(iso, "yyyy-MM-dd'T'HH:mm") : '');

// 'yyyy-MM-ddTHH:mm' typed as Pacific wall-clock -> UTC ISO string
export const businessInputToIso = (local) => {
  if (!local) return null;
  const [datePart, timePart] = local.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [h, mi] = timePart.split(':').map(Number);
  const target = Date.UTC(y, m - 1, d, h, mi);
  let guess = target;
  for (let i = 0; i < 2; i += 1) {
    const offset = Date.UTC(...zonedParts(new Date(guess))) - guess;
    guess = target - offset;
  }
  return new Date(guess).toISOString();
};

export const todayInBusinessTz = () => fmtTz(new Date(), 'yyyy-MM-dd');
